import { useSyncExternalStore } from 'react';
import type { Currency } from 'shared';
import { api } from './api';
import { on, syncClock } from './realtime';

/**
 * Торги, в которых участвует этот человек.
 *
 * Хранилище, а не контекст, по той же причине, что и у ширины меню
 * (`navWidth.ts`): состояние нужно и полоске обратного отсчёта, живущей
 * рядом с навигацией, и карточке груза, которая должна мгновенно
 * запереть форму ставки при закрытии торгов. Через контекст пришлось бы
 * поднимать провайдер выше `App` ради одного массива.
 *
 * Наполняется из двух источников: разовый запрос при входе (события не
 * рассказывают о том, что было до подключения) и события `auction:*`.
 */

export interface AuctionSeat {
  cargoId: string;
  title: string;
  currency: Currency;
  /** Момент закрытия торгов по часам сервера. */
  bidEndsAt: number | null;
  bestBid: number | null;
  myBid: number | null;
  leading: boolean;
  /** Какую ставку примут следующей. null — предлагать нечего. */
  nextBid: number | null;
  /** Торги закрылись: полоска показывает исход и уходит. */
  closed: null | { won: boolean; orderId: string | null; winningBid: number | null };
}

/** Сколько держим итоговую плашку после закрытия торгов. */
const CLOSED_LINGER_MS = 20_000;

let seats: AuctionSeat[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/**
 * Порядок — по близости срока: первым тот, где решать надо раньше.
 * Закрытые уходят вниз, они уже ничего не требуют.
 */
function sorted(rows: AuctionSeat[]): AuctionSeat[] {
  return [...rows].sort((a, b) => {
    if (Boolean(a.closed) !== Boolean(b.closed)) return a.closed ? 1 : -1;
    return (a.bidEndsAt ?? Infinity) - (b.bidEndsAt ?? Infinity);
  });
}

function put(seat: AuctionSeat) {
  const at = seats.findIndex((s) => s.cargoId === seat.cargoId);
  seats = at >= 0 ? seats.map((s, i) => (i === at ? seat : s)) : [...seats, seat];
  seats = sorted(seats);
  emit();
}

export function dropAuction(cargoId: string) {
  const next = seats.filter((s) => s.cargoId !== cargoId);
  if (next.length === seats.length) return;
  seats = next;
  emit();
}

export function clearAuctions() {
  if (seats.length === 0) return;
  seats = [];
  emit();
}

/** Состояние конкретных торгов — карточке груза. */
export function auctionSeat(cargoId: string): AuctionSeat | null {
  return seats.find((s) => s.cargoId === cargoId) ?? null;
}

/**
 * Подтянуть свои живые торги.
 *
 * Вызывается при входе и при возвращении вкладки: пока приложение было
 * закрыто, ставки могли перебить, а события до нас не дошли.
 */
export async function loadAuctions(): Promise<void> {
  try {
    const r = await api.myBids();
    syncClock(r.now);
    seats = sorted(
      r.items.map((i) => ({
        cargoId: i.cargoId,
        title: i.title,
        currency: i.currency,
        bidEndsAt: i.bidEndsAt ? new Date(i.bidEndsAt).getTime() : null,
        bestBid: i.bestBid,
        myBid: i.myBid,
        leading: i.leading,
        nextBid: i.nextBid,
        closed: null,
      })),
    );
    emit();
  } catch {
    // Нет торгов — нет полоски. Пустой экран лучше сообщения об ошибке
    // там, где человек ничего не спрашивал.
  }
}

let wired = false;

/**
 * Подписка на события торгов. Одна на всё приложение — вешается при
 * первом обращении и живёт до перезагрузки.
 */
export function wireAuctions(onOutbid?: (seat: AuctionSeat) => void) {
  if (wired) return;
  wired = true;

  on('auction:update', (e) => {
    syncClock(e.now);
    const before = seats.find((s) => s.cargoId === e.cargoId) ?? null;

    const seat: AuctionSeat = {
      cargoId: e.cargoId,
      title: e.title,
      currency: e.currency,
      bidEndsAt: e.bidEndsAt ? new Date(e.bidEndsAt).getTime() : null,
      bestBid: e.bestBid,
      myBid: e.myBid,
      leading: e.leading,
      nextBid: e.nextBid,
      closed: null,
    };
    put(seat);

    // Сигналим только на переход «вёл → перебили»: повторять отклик на
    // каждое обновление значит превратить его в фон, который не замечают.
    if (before?.leading && !seat.leading) onOutbid?.(seat);
  });

  on('auction:closed', (e) => {
    const before = seats.find((s) => s.cargoId === e.cargoId);
    put({
      cargoId: e.cargoId,
      title: e.title,
      currency: e.currency,
      bidEndsAt: null,
      bestBid: before?.bestBid ?? null,
      myBid: before?.myBid ?? null,
      leading: e.won,
      nextBid: null,
      closed: { won: e.won, orderId: e.orderId, winningBid: e.winningBid },
    });

    // Итог держим недолго и убираем сами: полоска — уведомление, а не
    // список дел. Победа при этом остаётся в рейсах, проигрыш в ленте.
    window.setTimeout(() => dropAuction(e.cargoId), CLOSED_LINGER_MS);
  });

  // Груз ушёл по любой другой причине — снят автором, протух: торги по
  // нему больше не идут, и таймер обязан исчезнуть вместе с ним.
  on('cargo:closed', (e) => {
    const seat = seats.find((s) => s.cargoId === e.cargoId);
    if (seat && !seat.closed) dropAuction(e.cargoId);
  });
}

export function useAuctions(): AuctionSeat[] {
  return useSyncExternalStore(
    subscribe,
    () => seats,
    () => seats,
  );
}
